import { useCallback, useEffect, useState } from "react";
import {
  UnauthorizedError,
  createOrder,
  fetchKeyInfo,
  fetchRenewPrice,
} from "../api/licencesApi";
import { getExtensionsValue, normalizeEdition } from "../utils/licenceUtils";
import { AUTH_STORAGE_KEYS, getAuthValue } from "../api/authApi";

const EDITION_RANK = {
  Basic: 1,
  Professional: 2,
  Enterprise: 3,
};

function isUpgradeEdition(fromEdition, toEdition) {
  const from = normalizeEdition(fromEdition);
  const to = normalizeEdition(toEdition);
  return (EDITION_RANK[to] || 0) > (EDITION_RANK[from] || 0);
}

export function useRenewFlow({ onUnauthorized, partners = [] }) {
  const [licence, setLicence] = useState(null);
  const [years, setYears] = useState(1);
  const [edition, setEdition] = useState("Basic");
  const [addHosting, setAddHosting] = useState(false);
  const [hostingLocked, setHostingLocked] = useState(false);
  const [simultaneousCalls, setSimultaneousCalls] = useState(8);

  const [priceResult, setPriceResult] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState("");

  const [keyInfoLoading, setKeyInfoLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const open = Boolean(licence);

  const start = useCallback(
    (l) => {
      setLicence(l);
      setYears(1);
      setEdition(normalizeEdition(l.Edition));
      setAddHosting(false);
      setHostingLocked(false);
      setSimultaneousCalls(Number(l?.SimultaneousCalls) || 8);
      setPriceResult(null);
      setPriceError("");
      setError("");
      setSuccess("");

      setKeyInfoLoading(true);
      fetchKeyInfo(l.LicenseKey)
        .then((info) => {
          if (info?.hostingYears) {
            setAddHosting(true);
            setHostingLocked(true);
          }
        })
        .catch((err) => {
          if (err instanceof UnauthorizedError) {
            onUnauthorized();
            return;
          }
          console.error("fetchKeyInfo error", err);
        })
        .finally(() => {
          setKeyInfoLoading(false);
        });
    },
    [onUnauthorized],
  );

  const close = useCallback(() => {
    if (submitting) return;
    setLicence(null);
  }, [submitting]);

  useEffect(() => {
    if (!licence) return undefined;

    let cancelled = false;
    setPriceLoading(true);
    setPriceError("");

    const fromEdition = normalizeEdition(licence.Edition);
    const toEdition = normalizeEdition(edition);
    const renewType = isUpgradeEdition(fromEdition, toEdition)
      ? "Upgrade"
      : "RenewAnnual";

    fetchRenewPrice({
      licenseKey: licence.LicenseKey,
      simultaneousCalls: Number(simultaneousCalls) || 0,
      edition: toEdition,
      years: Number(years) || 1,
      IsPerpetual: Boolean(licence.IsPerpetual),
      Extensions: getExtensionsValue(licence),
      AddHosting: addHosting ? true : null,
      FromEdition: fromEdition,
      ToEdition: toEdition,
      Type: renewType,
    })
      .then((result) => {
        if (!cancelled) setPriceResult(result || null);
      })
      .catch((err) => {
        console.log("fetchRenewPrice error", err);
        if (cancelled) return;
        if (err instanceof UnauthorizedError) {
          onUnauthorized();
          return;
        }
        console.error(err);
        setPriceResult(null);
        setPriceError(err.message || "Failed to fetch renew price");
      })
      .finally(() => {
        if (!cancelled) setPriceLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [licence, years, edition, addHosting, simultaneousCalls, onUnauthorized]);

  const submit = useCallback(async () => {
    if (!licence) return;

    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const fromEdition = normalizeEdition(licence.Edition);
      const toEdition = normalizeEdition(edition);
      const q = Number(years) || 1;

      const resellerId = licence?.ResellerID;

      const partner = partners.find(
        (item) => String(item?.PartnerId) === String(resellerId),
      );

      const partnerEmail = partner?.ContactEmail || "";

      const payload = {
        Type: isUpgradeEdition(fromEdition, toEdition)
          ? "Upgrade"
          : "RenewAnnual",
        Edition: toEdition,
        Quantity: q,
        UpgradeKey: licence.LicenseKey,
        AddHosting: Boolean(addHosting),
        SimultaneousCalls: Number(simultaneousCalls) || undefined,
        PartnerId: resellerId,
        PartnerEmail: partnerEmail,
        Price: priceResult?.price ?? priceResult?.Price,
      };

      const result = await createOrder(payload);
      console.log("create-order payload", payload, "result", result);

      const ok =
        result === true ||
        result?.ok === true ||
        result?.success === true ||
        result?.data === true;

      if (!ok) {
        throw new Error("create-order did not return true");
      }

      setSuccess("Order created successfully.");
      setLicence(null);
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      console.error(err);
      setError(err.message || "Failed to create order");
    } finally {
      setSubmitting(false);
    }
  }, [
    licence,
    years,
    edition,
    addHosting,
    simultaneousCalls,
    priceResult,
    partners,
    onUnauthorized,
  ]);

  return {
    open,
    licence,
    years,
    setYears,
    edition,
    setEdition,
    addHosting,
    setAddHosting,
    hostingLocked,
    keyInfoLoading,
    simultaneousCalls,
    setSimultaneousCalls,
    priceResult,
    priceLoading,
    priceError,
    submitting,
    error,
    success,
    setError,
    setSuccess,
    start,
    close,
    submit,
  };
}
